/** SUNTRONIX ONE — Universal Create Centre.
 *  A navigation/orchestration layer over existing module create routes.
 *  Search · Pinned · Recent · role-aware categories. Never grants new
 *  permissions — underlying routes enforce security server-side. */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import {
  FileText, Zap, ShoppingCart, ClipboardList, PhoneCall, Building2, UserPlus,
  Store, MapPin, Box, Wrench, Tag, Layers, BookOpen, ArrowDownToLine,
  ArrowUpFromLine, ArrowLeftRight, Undo2, SlidersHorizontal, Truck, Laptop2,
  Rocket, Warehouse, Users, Receipt, Wallet, CreditCard, Landmark, ShieldCheck,
  LifeBuoy, Mail, Megaphone, FileSignature, StickyNote, CheckSquare, Search,
  Pin, PinOff, Clock, Sparkles, FolderLock, Upload, MessageCircle,
} from "lucide-react";

// role buckets ----------------------------------------------------------------
const R_SALES = ["super_admin", "management", "sales_coordinator", "operations", "account_manager", "sales"];
const R_OPS = ["super_admin", "management", "operations", "stores", "warehouse", "device_manager", "packaging", "delivery"];
const R_STORE = ["super_admin", "management", "operations", "stores", "warehouse"];
const R_FIN = ["super_admin", "management", "finance"];
const R_PROC = ["super_admin", "management", "procurement", "operations"];
const R_PEOPLE = ["super_admin", "management", "operations", "customer_experience"];
const R_ADMIN = ["super_admin", "management"];
const R_SVC = ["super_admin", "management", "operations", "service", "device_manager"];
const R_COMMS = ["super_admin", "management", "sales_coordinator", "customer_experience"];

// registry --------------------------------------------------------------------
// { id, label, to, category, icon, roles }  — roles omitted = all internal users
const REGISTRY = [
  // SALES
  { id: "quote", label: "Quotation", to: "/commercial/crm/quotations?new=1", category: "SALES", icon: FileText, roles: R_SALES },
  { id: "customer-po", label: "Customer PO", to: "/commercial/customer-po?new=1", category: "SALES", icon: ShoppingCart, roles: R_SALES },
  { id: "order", label: "Order", to: "/commercial/one-orders?new=1", category: "SALES", icon: ClipboardList, roles: R_SALES },
  { id: "followup", label: "Follow-Up", to: "/commercial/followup?new=1", category: "SALES", icon: PhoneCall, roles: R_SALES },
  { id: "walkin-feedback", label: "Walk-in / Service Feedback", to: "/sales/walkin-feedback?new=1", category: "SALES", icon: MessageCircle, roles: [...R_SALES, "service", "customer_experience", "device_manager"] },
  { id: "whatsapp-message", label: "WhatsApp Message", to: "/admin/integrations/whatsapp?compose=1", category: "SALES", icon: MessageCircle, roles: [...R_SALES, "finance", "service", "device_manager"] },
  // CUSTOMERS
  { id: "customer", label: "Customer", to: "/commercial/crm/customers?new=1", category: "CUSTOMERS", icon: Building2, roles: R_SALES },
  { id: "contact", label: "Contact", to: "/commercial/crm/customers?new=1&contact=1", category: "CUSTOMERS", icon: UserPlus, roles: R_SALES },
  { id: "dealer", label: "Dealer / Reseller", to: "/commercial/crm/customers?new=1&relationship=DEALER", category: "CUSTOMERS", icon: Store, roles: R_SALES },
  { id: "address", label: "Address / Site", to: "/admin/sites?new=1", category: "CUSTOMERS", icon: MapPin, roles: R_SALES },
  // PRODUCTS & SERVICES
  { id: "product", label: "Product", to: "/technology/product-master?new=1", category: "PRODUCTS & SERVICES", icon: Box, roles: R_SALES },
  { id: "service", label: "Service", to: "/technology/service-master?new=1", category: "PRODUCTS & SERVICES", icon: Wrench, roles: R_SALES },
  { id: "brand", label: "Brand", to: "/catalogue-v2", category: "PRODUCTS & SERVICES", icon: Tag, roles: R_ADMIN },
  { id: "model", label: "Product / Model", to: "/catalogue-v2", category: "PRODUCTS & SERVICES", icon: Box, roles: R_ADMIN },
  { id: "category", label: "Category", to: "/admin/masters/product_category?new=1", category: "PRODUCTS & SERVICES", icon: Layers, roles: R_ADMIN },
  { id: "pricebook", label: "Price Book", to: "/admin/masters/price_book?new=1", category: "PRODUCTS & SERVICES", icon: BookOpen, roles: R_ADMIN },
  // OPERATIONS
  { id: "stock-in", label: "Stock In", to: "/operations/stock-inventory?action=stock-in", category: "OPERATIONS", icon: ArrowDownToLine, roles: R_STORE },
  { id: "stock-out", label: "Stock Out", to: "/operations/stock-inventory?action=stock-out", category: "OPERATIONS", icon: ArrowUpFromLine, roles: R_STORE },
  { id: "transfer", label: "Transfer", to: "/operations/stock-inventory?tab=Transfers&action=new", category: "OPERATIONS", icon: ArrowLeftRight, roles: R_STORE },
  { id: "return", label: "Return", to: "/operations/stock-inventory?tab=Returns&action=new", category: "OPERATIONS", icon: Undo2, roles: R_STORE },
  { id: "stock-adjust", label: "Stock Adjustment", to: "/operations/daily-stock?action=adjustment", category: "OPERATIONS", icon: SlidersHorizontal, roles: R_STORE },
  { id: "shipment", label: "Shipment", to: "/shipments?new=1", category: "OPERATIONS", icon: Truck, roles: R_OPS },
  { id: "asset", label: "Asset", to: "/assets?new=1", category: "OPERATIONS", icon: Laptop2, roles: R_OPS },
  { id: "deploy", label: "Deploy Device", to: "/deploy", category: "OPERATIONS", icon: Rocket, roles: R_OPS },
  { id: "recovery", label: "Recovery", to: "/offboarding?new=1", category: "OPERATIONS", icon: Undo2, roles: R_OPS },
  { id: "warehouse", label: "Warehouse", to: "/operations/stock-inventory?tab=Warehouses&action=new", category: "OPERATIONS", icon: Warehouse, roles: R_ADMIN },
  // PROCUREMENT
  { id: "vendor", label: "Vendor", to: "/admin/masters/vendor?new=1", category: "PROCUREMENT", icon: Building2, roles: R_PROC },
  { id: "vendor-po", label: "Vendor PO", to: "/commercial/crm/vendor-pos?new=1", category: "PROCUREMENT", icon: ShoppingCart, roles: R_PROC },
  { id: "grn", label: "GRN / Inward", to: "/operations/stock-inventory?action=grn", category: "PROCUREMENT", icon: ArrowDownToLine, roles: R_PROC },
  // FINANCE
  { id: "post-txn", label: "Post Transaction", to: "/one-post", category: "FINANCE", icon: Zap, roles: R_FIN },
  { id: "invoice", label: "Invoice", to: "/invoices?new=1", category: "FINANCE", icon: Receipt, roles: R_FIN },
  { id: "payment", label: "Payment", to: "/finance/cash-control?action=payment", category: "FINANCE", icon: Wallet, roles: R_FIN },
  { id: "cash-txn", label: "Cash Transaction", to: "/finance/cash-control?action=add-txn", category: "FINANCE", icon: Wallet, roles: R_FIN },
  { id: "cash-account", label: "Cash Account", to: "/finance/cash-control?action=add-account", category: "FINANCE", icon: Landmark, roles: R_FIN },
  { id: "card", label: "Credit Card", to: "/finance/cards?action=add-card", category: "FINANCE", icon: CreditCard, roles: R_FIN },
  { id: "card-txn", label: "Card Transaction", to: "/finance/cards?action=add-txn", category: "FINANCE", icon: CreditCard, roles: R_FIN },
  // SERVICES
  { id: "applecare", label: "AppleCare+", to: "/operations/applecare-plus?new=1", category: "SERVICES", icon: ShieldCheck, roles: R_SVC },
  { id: "apple-enroll", label: "Apple Device Enrollment", to: "/operations/apple-services?action=enroll", category: "SERVICES", icon: Laptop2, roles: R_SVC },
  { id: "support-case", label: "Support Ticket", to: "/service-desk?new=1", category: "SERVICES", icon: LifeBuoy },
  { id: "pickup", label: "Pickup", to: "/pickups?new=1", category: "OPERATIONS", icon: Truck, roles: R_OPS },
  { id: "delivery-challan", label: "Delivery Challan", to: "/commercial/one-orders?dc=1", category: "OPERATIONS", icon: ClipboardList, roles: R_OPS },
  { id: "task", label: "Task", to: "/work/projects?new=1", category: "MY WORK", icon: Zap },
  { id: "rfq", label: "RFQ / Enquiry", to: "/sales-desk/catalogue-enquiries?new=1", category: "SALES", icon: FileText, roles: R_SALES },
  { id: "rma", label: "Repair / RMA", to: "/rma?new=1", category: "SERVICES", icon: Wrench, roles: R_SVC },
  // PEOPLE
  { id: "employee", label: "Employee", to: "/employees?new=1", category: "PEOPLE", icon: Users, roles: R_PEOPLE },
  { id: "user-invite", label: "User Invitation", to: "/users?invite=1", category: "PEOPLE", icon: UserPlus, roles: R_ADMIN },
  // COMMUNICATIONS
  { id: "email", label: "Customer Communication", to: "/commercial/one-engage?new=1", category: "COMMUNICATIONS", icon: Mail, roles: R_COMMS },
  { id: "campaign", label: "Campaign", to: "/commercial/whatsapp-campaigns?new=1", category: "COMMUNICATIONS", icon: Megaphone, roles: R_COMMS },
  { id: "template", label: "Template", to: "/admin/communications?new=1", category: "COMMUNICATIONS", icon: FileSignature, roles: R_ADMIN },
  // DOCUMENTS
  { id: "doc-upload", label: "Upload Document", to: "/vault?action=upload", category: "DOCUMENTS", icon: Upload },
  { id: "inbox-open", label: "Open ONE Inbox", to: "/vault/inbox", category: "DOCUMENTS", icon: FileText },
  { id: "doc-business", label: "Business Document", to: "/vault?action=upload&vault=BUSINESS", category: "DOCUMENTS", icon: FolderLock },
  { id: "doc-personal", label: "Personal Document", to: "/vault?action=upload&vault=PERSONAL", category: "DOCUMENTS", icon: FolderLock },
  { id: "doc-family", label: "Family Document", to: "/vault?action=upload&vault=FAMILY", category: "DOCUMENTS", icon: FolderLock },
  // DATA / MIGRATION
  { id: "import-excel", label: "Import Excel", to: "/imports", category: "DATA", icon: Upload, roles: ["super_admin", "management", "operations", "finance", "stores"] },
  // OTHER
  { id: "request", label: "Request", to: "/requests?new=1", category: "OTHER", icon: ClipboardList },
  { id: "note", label: "Note", to: "/relationships?new=note", category: "OTHER", icon: StickyNote },
  { id: "task", label: "Task", to: "/work?new=task", category: "OTHER", icon: CheckSquare },
  { id: "relationship", label: "Relationship", to: "/relationships?new=1", category: "OTHER", icon: Users },
];

const CATEGORY_ORDER = ["DATA", "SALES", "CUSTOMERS", "PRODUCTS & SERVICES", "OPERATIONS",
  "PROCUREMENT", "FINANCE", "SERVICES", "PEOPLE", "COMMUNICATIONS", "DOCUMENTS", "OTHER"];

const ROLE_DEFAULTS = {
  sales_coordinator: ["quote", "customer", "contact", "product", "followup", "customer-po"],
  operations: ["stock-in", "stock-out", "transfer", "shipment", "deploy"],
  finance: ["invoice", "payment", "cash-txn", "cash-account"],
  stores: ["stock-in", "stock-out", "transfer", "return"],
  warehouse: ["stock-in", "stock-out", "transfer", "return"],
  procurement: ["vendor", "vendor-po", "grn"],
};

const CUSTOMER_ACTIONS = [
  { id: "c-deploy", label: "Onboard employee", to: "/employees", category: "OTHER", icon: UserPlus },
  { id: "c-recover", label: "Recover technology", to: "/pickups", category: "OTHER", icon: Undo2 },
  { id: "c-support", label: "Request support", to: "/services", category: "OTHER", icon: LifeBuoy },
  { id: "c-order", label: "Order technology", to: "/catalogue", category: "OTHER", icon: ShoppingCart },
];

const pinsKey = (role) => `st.uc.pins.${role || "x"}`;
const recentKey = (role) => `st.uc.recent.${role || "x"}`;
const readLS = (k) => { try { return JSON.parse(localStorage.getItem(k) || "[]"); } catch { return []; } };
const writeLS = (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch { /* noop */ } };

/** Persistent top-bar one-click actions — open the SAME full forms as Create → Quotation / Product (one quote flow). */
export function InstantCreate({ role, compact = false }) {
  const nav = useNavigate();
  if (!R_SALES.includes(role)) return null;
  const b = "h-9 rounded-full border border-[color:var(--st-border)] bg-white px-3 text-[12px] font-semibold text-[color:var(--st-text)] hover:border-[#00AFF0] hover:text-[#0077B0] flex items-center gap-1.5 transition-colors";
  return (
    <div className="flex items-center gap-1.5" data-testid="instant-create">
      <button className={b} onClick={() => nav("/commercial/crm/quotations?new=1")} title="New Quote" aria-label="New Quote" data-testid="topbar-new-quote"><FileText className="h-4 w-4" /><span className={compact ? "hidden 2xl:inline whitespace-nowrap" : "hidden sm:inline whitespace-nowrap"}>New Quote</span></button>
      <button className={b} onClick={() => nav("/technology/product-master?new=1")} title="Add Product" aria-label="Add Product" data-testid="topbar-add-product"><Box className="h-4 w-4" /><span className={compact ? "hidden 2xl:inline whitespace-nowrap" : "hidden sm:inline whitespace-nowrap"}>Add Product</span></button>
    </div>
  );
}

export default function UniversalCreate({ open, onOpenChange, role, isCustomer }) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [pins, setPins] = useState([]);
  const [recent, setRecent] = useState([]);
  const inputRef = useRef(null);

  const available = useMemo(() => {
    if (isCustomer) return CUSTOMER_ACTIONS;
    return REGISTRY.filter((a) => !a.roles || a.roles.includes(role));
  }, [role, isCustomer]);

  useEffect(() => {
    if (!open) { setQ(""); return; }
    setPins(readLS(pinsKey(role)));
    setRecent(readLS(recentKey(role)));
    setTimeout(() => inputRef.current?.focus(), 60);
  }, [open, role]);

  const byId = useMemo(() => Object.fromEntries(available.map((a) => [a.id, a])), [available]);
  const defaults = useMemo(() => (ROLE_DEFAULTS[role] || []).map((id) => byId[id]).filter(Boolean), [role, byId]);

  const run = (a) => {
    const next = [a.id, ...recent.filter((x) => x !== a.id)].slice(0, 6);
    writeLS(recentKey(role), next);
    onOpenChange(false);
    navigate(a.to);
  };
  const togglePin = (e, id) => {
    e.stopPropagation();
    const next = pins.includes(id) ? pins.filter((x) => x !== id) : [...pins, id].slice(0, 8);
    setPins(next); writeLS(pinsKey(role), next);
  };

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return null;
    return available.filter((a) => a.label.toLowerCase().includes(s) || a.category.toLowerCase().includes(s) || a.id.includes(s));
  }, [q, available]);

  const pinItems = pins.map((id) => byId[id]).filter(Boolean);
  const recentItems = recent.map((id) => byId[id]).filter(Boolean);
  const showDefaults = !pinItems.length && !recentItems.length && defaults.length > 0;

  const Row = ({ a }) => {
    const Icon = a.icon || Sparkles;
    const pinned = pins.includes(a.id);
    return (
      <div className="group w-full flex items-center gap-2.5 px-2.5 py-2 rounded-md hover:bg-emerald-50/70">
        <button onClick={() => run(a)} data-testid={`uc-action-${a.id}`}
          className="flex items-center gap-2.5 flex-1 min-w-0 text-left">
          <span className="h-7 w-7 rounded-md bg-slate-100 grid place-items-center text-slate-600 group-hover:bg-emerald-100 group-hover:text-emerald-800 shrink-0">
            <Icon className="h-4 w-4" strokeWidth={1.8} />
          </span>
          <span className="flex-1 text-[13px] text-slate-800 truncate">{a.label}</span>
        </button>
        {!isCustomer && (
          <button type="button" onClick={(e) => togglePin(e, a.id)} data-testid={`uc-pin-${a.id}`}
            aria-label={pinned ? `Unpin ${a.label}` : `Pin ${a.label}`}
            className={`p-1 shrink-0 ${pinned ? "text-emerald-700" : "text-slate-300 hover:text-emerald-700 md:opacity-40 md:group-hover:opacity-100"}`}>
            {pinned ? <PinOff className="h-3.5 w-3.5" /> : <Pin className="h-3.5 w-3.5" />}
          </button>
        )}
      </div>
    );
  };

  const Section = ({ title, icon: Ic, items }) => items.length ? (
    <div className="mb-2">
      <div className="px-2.5 py-1 text-[10px] uppercase tracking-[0.14em] text-slate-400 flex items-center gap-1.5">
        {Ic && <Ic className="h-3 w-3" />}{title}
      </div>
      {items.map((a) => <Row key={a.id} a={a} />)}
    </div>
  ) : null;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) setTimeout(() => onOpenChange(false), 120); }}>
      <DialogContent className="max-w-lg p-0 gap-0 overflow-hidden" data-testid="universal-create">
        <DialogTitle className="sr-only">Universal Create</DialogTitle>
        <div className="p-3 border-b border-slate-100">
          <div className="flex items-center gap-2 h-10 px-3 rounded-md border border-slate-200 bg-slate-50">
            <Search className="h-4 w-4 text-slate-400" />
            <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search to create… (e.g. Product, Cash, Quote)"
              data-testid="uc-search"
              className="flex-1 bg-transparent outline-none text-[13px]" />
          </div>
        </div>
        <div className="max-h-[62vh] overflow-y-auto p-2">
          {filtered ? (
            filtered.length ? filtered.map((a) => <Row key={a.id} a={a} />)
              : <div className="px-3 py-8 text-center text-[12px] text-slate-400" data-testid="uc-no-results">No create actions match “{q}”.</div>
          ) : (
            <>
              <Section title="Pinned" icon={Pin} items={pinItems} />
              <Section title="Recent" icon={Clock} items={recentItems} />
              {isCustomer ? (
                <Section title="Quick actions" icon={Sparkles} items={available} />
              ) : (
                <>
                  {showDefaults && <Section title="Recommended for you" icon={Sparkles} items={defaults} />}
                  {CATEGORY_ORDER.map((cat) => (
                    <Section key={cat} title={cat} items={available.filter((a) => a.category === cat)} />
                  ))}
                </>
              )}
              {!available.length && <div className="px-3 py-8 text-center text-[12px] text-slate-400">No create actions available for your role.</div>}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
